import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as api from '../../api/api.js';
import { toast } from 'react-toastify';

// Utility function to sanitize strategy names for CSS selectors
const sanitizeId = (str) => {
    return str.replace(/\s+/g, '-').toLowerCase();
};

const AlgoDashboard = () => {
    const [status, setStatus] = useState({ trading_active: false, accounts: {} });
    const [strategyConfigs, setStrategyConfigs] = useState({});
    const [formData, setFormData] = useState({});
    const strategies = ['Gold EMA 1', 'Gold EMA 2', 'Gold EMA 30150', 'Gold 4 Candles'];

    // Fetch status and strategy configs
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await api.getStatus();
                setStatus(response.data || { trading_active: false, accounts: {} });
            } catch (error) {
                console.error('Failed to fetch status:', error);
                toast.error('Failed to fetch trading status.');
            }
        };

        const fetchStrategyConfigs = async () => {
            try {
                const response = await api.getStrategyConfigs();
                const configs = response.data.reduce((acc, config) => ({
                    ...acc,
                    [config.strategy_name]: config.config
                }), {});
                setStrategyConfigs(configs);
                setFormData(configs);
            } catch (error) {
                console.error('Failed to fetch strategy configs:', error);
                toast.error('Failed to fetch strategy configurations.');
            }
        };

        // Initial fetch
        fetchStatus();
        fetchStrategyConfigs();

        // Poll status every 10 seconds
        const interval = setInterval(fetchStatus, 10000);
        return () => clearInterval(interval);
    }, []);

    // Handle form input changes
    const handleInputChange = (strategy, field, value) => {
        setFormData(prev => ({
            ...prev,
            [strategy]: {
                ...prev[strategy],
                [field]: value
            }
        }));
    };

    // Handle form submission to update strategy config
    const handleSubmit = async (strategy, e) => {
        e.preventDefault();
        // Check if any process for this strategy is running
        const isAnyProcessRunning = Object.keys(status.accounts).some(
            key => key.includes(strategy) && status.accounts[key].is_running
        );
        if (status.trading_active || isAnyProcessRunning) {
            toast.error('Cannot update configuration while trading is active or strategy processes are running.');
            return;
        }
        try {
            await api.updateStrategyConfig(strategy, formData[strategy]);
            toast.success('Strategy configuration updated successfully!');
            setStrategyConfigs(prev => ({
                ...prev,
                [strategy]: formData[strategy]
            }));
        } catch (error) {
            console.error('Failed to update strategy config:', error);
            toast.error('Failed to update configuration.');
        }
    };

    // Determine if a strategy has any running processes
    const getStrategyStatus = (strategy) => {
        const relatedAccounts = Object.keys(status.accounts).filter(key => key.includes(strategy));
        return relatedAccounts.some(key => status.accounts[key].is_running);
    };

    return (
        <div className="App container mt-4">
            <h1>Algo Trading Dashboard</h1>

            {/* Bootstrap Tabs for Strategies */}
            <ul className="nav nav-tabs mb-4">
                {strategies.map((strategy, index) => (
                    <li className="nav-item" key={strategy}>
                        <a
                            className={`nav-link ${index === 0 ? 'active' : ''}`}
                            id={`${sanitizeId(strategy)}-tab`}
                            data-bs-toggle="tab"
                            href={`#${sanitizeId(strategy)}`}
                            role="tab"
                            aria-controls={sanitizeId(strategy)}
                            aria-selected={index === 0}
                        >
                            {strategy}
                            <span className={`ms-2 badge`}></span>
                        </a>
                    </li>
                ))}
            </ul>

            {/* Tab Content */}
            <div className="tab-content">
                {strategies.map((strategy, index) => (
                    <div
                        className={`tab-pane fade ${index === 0 ? 'show active' : ''}`}
                        id={sanitizeId(strategy)}
                        role="tabpanel"
                        aria-labelledby={`${sanitizeId(strategy)}-tab`}
                        key={strategy}
                    >
                        <h2>{strategy} Strategy</h2>

                        <form onSubmit={(e) => handleSubmit(strategy, e)} className="mt-3">
                            {strategy === 'Gold EMA 1' && (
                                <>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-emaPeriod`} className="form-label">EMA Period</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-emaPeriod`}
                                            value={formData[strategy]?.ema_period || 9}
                                            onChange={(e) => handleInputChange(strategy, 'ema_period', parseInt(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            min="1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-timeframe`} className="form-label">Timeframe</label>
                                        <select
                                            className="form-select"
                                            id={`${sanitizeId(strategy)}-timeframe`}
                                            value={formData[strategy]?.timeframe || 'M5'}
                                            onChange={(e) => handleInputChange(strategy, 'timeframe', e.target.value)}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                        >
                                            <option value="M1">M1</option>
                                            <option value="M5">M5</option>
                                            <option value="M15">M15</option>
                                            <option value="M30">M30</option>
                                            <option value="H1">H1</option>
                                            <option value="H4">H4</option>
                                            <option value="D1">D1</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-lotSize`} className="form-label">Lot Size</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-lotSize`}
                                            value={formData[strategy]?.lot_size || 1.0}
                                            onChange={(e) => handleInputChange(strategy, 'lot_size', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.01"
                                            min="0.01"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-tpMultiplier`} className="form-label">TP Multiplier</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-tpMultiplier`}
                                            value={formData[strategy]?.tp_multiplier || 1.5}
                                            onChange={(e) => handleInputChange(strategy, 'tp_multiplier', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.1"
                                            min="0.1"
                                        />
                                    </div>
                                </>
                            )}
                            {strategy === 'Gold EMA 2' && (
                                <>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-emaPeriod`} className="form-label">EMA Period</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-emaPeriod`}
                                            value={formData[strategy]?.ema_period || 30}
                                            onChange={(e) => handleInputChange(strategy, 'ema_period', parseInt(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            min="1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-timeframe`} className="form-label">Timeframe</label>
                                        <select
                                            className="form-select"
                                            id={`${sanitizeId(strategy)}-timeframe`}
                                            value={formData[strategy]?.timeframe || 'M5'}
                                            onChange={(e) => handleInputChange(strategy, 'timeframe', e.target.value)}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                        >
                                            <option value="M1">M1</option>
                                            <option value="M5">M5</option>
                                            <option value="M15">M15</option>
                                            <option value="M30">M30</option>
                                            toppings <option value="H1">H1</option>
                                            <option value="H4">H4</option>
                                            <option value="D1">D1</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-lotSize`} className="form-label">Lot Size</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-lotSize`}
                                            value={formData[strategy]?.lot_size || 1}
                                            onChange={(e) => handleInputChange(strategy, 'lot_size', parseInt(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="1"
                                            min="1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-tpMultiplier`} className="form-label">TP Multiplier</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-tpMultiplier`}
                                            value={formData[strategy]?.tp_multiplier || 2.0}
                                            onChange={(e) => handleInputChange(strategy, 'tp_multiplier', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.1"
                                            min="0.1"
                                        />
                                    </div>
                                </>
                            )}
                            {strategy === 'Gold EMA 30150' && (
                                <>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-emaPeriod`} className="form-label">EMA Period</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-emaPeriod`}
                                            value={formData[strategy]?.ema_period || 9}
                                            onChange={(e) => handleInputChange(strategy, 'ema_period', parseInt(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            min="1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-timeframe`} className="form-label">Timeframe</label>
                                        <select
                                            className="form-select"
                                            id={`${sanitizeId(strategy)}-timeframe`}
                                            value={formData[strategy]?.timeframe || 'M5'}
                                            onChange={(e) => handleInputChange(strategy, 'timeframe', e.target.value)}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                        >
                                            <option value="M1">M1</option>
                                            <option value="M5">M5</option>
                                            <option value="M15">M15</option>
                                            <option value="M30">M30</option>
                                            <option value="H1">H1</option>
                                            <option value="H4">H4</option>
                                            <option value="D1">D1</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-lotSize`} className="form-label">Lot Size</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-lotSize`}
                                            value={formData[strategy]?.lot_size || 1.0}
                                            onChange={(e) => handleInputChange(strategy, 'lot_size', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.01"
                                            min="0.01"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-tpMultiplier`} className="form-label">TP Multiplier</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-tpMultiplier`}
                                            value={formData[strategy]?.tp_multiplier || 2.0}
                                            onChange={(e) => handleInputChange(strategy, 'tp_multiplier', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.1"
                                            min="0.1"
                                        />
                                    </div>
                                </>
                            )}
                            {strategy === 'Gold 4 Candles' && (
                                <>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-timeframe`} className="form-label">Timeframe</label>
                                        <select
                                            className="form-select"
                                            id={`${sanitizeId(strategy)}-timeframe`}
                                            value={formData[strategy]?.timeframe || 'M5'}
                                            onChange={(e) => handleInputChange(strategy, 'timeframe', e.target.value)}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                        >
                                            <option value="M1">M1</option>
                                            <option value="M5">M5</option>
                                            <option value="M15">M15</option>
                                            <option value="M30">M30</option>
                                            <option value="H1">H1</option>
                                            <option value="H4">H4</option>
                                            <option value="D1">D1</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-lotSize`} className="form-label">Lot Size</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-lotSize`}
                                            value={formData[strategy]?.lot_size || 1.0}
                                            onChange={(e) => handleInputChange(strategy, 'lot_size', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.01"
                                            min="0.01"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-targetPoints`} className="form-label">Target Points</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-targetPoints`}
                                            value={formData[strategy]?.target_points || 4.0}
                                            onChange={(e) => handleInputChange(strategy, 'target_points', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.1"
                                            min="0.1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-stopLossPoints`} className="form-label">Stop Loss Points</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-stopLossPoints`}
                                            value={formData[strategy]?.stop_loss_points || 3.0}
                                            onChange={(e) => handleInputChange(strategy, 'stop_loss_points', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.1"
                                            min="0.1"
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor={`${sanitizeId(strategy)}-maxWickPercentage`} className="form-label">Max Wick Percentage</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            id={`${sanitizeId(strategy)}-maxWickPercentage`}
                                            value={formData[strategy]?.max_wick_percentage || 0.2}
                                            onChange={(e) => handleInputChange(strategy, 'max_wick_percentage', parseFloat(e.target.value))}
                                            disabled={status.trading_active || getStrategyStatus(strategy)}
                                            step="0.01"
                                            min="0.0"
                                            max="1.0"
                                        />
                                    </div>
                                </>
                            )}
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={status.trading_active || getStrategyStatus(strategy)}
                            >
                                Update Configuration
                            </button>
                        </form>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AlgoDashboard;